/*
 * hp2026.js -- pohyb homepage přenesený 1:1 z mockupu (plán 11-05b, kolo 3).
 *
 * Zdroj je inline skript návrhu `navrh-th-hp.html` (ř. 1138-1300); tenhle
 * modul dělá totéž, jen jako ES modul v bundlu šablony a se scopem na
 * homepage (`.th2026-hp-mockup`). Osm chování, která audit koordinátora
 * 5. 9. 2026 označil za chybějící:
 *
 *   1. tooltip pinů mapy lokalit            (návrh ř. 1145-1161)
 *   2. showreel v přehrávači na kliknutí    (návrh ř. 1163-1177)
 *   3. plynulý drift pásů her a recenzí     (návrh ř. 1180-1204)
 *   4. nekonečný pás lokalit pod hero       (návrh ř. 1206-1241)
 *   5. samovolné střídání fotek v hero      (návrh ř. 1242-1258)
 *   6. vstupní animace hero                 (návrh ř. 1259-1264)
 *   7. nástup sekcí při scrollu             (návrh ř. 1265-1274)
 *   8. náběh čísel ze statistik             (návrh ř. 1275-1299)
 *
 * MOBIL POD 900 px MÁ VLASTNÍ CHOVÁNÍ. Zdroj pravdy je mobilní návrh
 * `navrh-th-mobil-hp.html` (rozhodnutí koordinátora 5. 9. 23:22): pásy
 * i karusely tam jedou NATIVNÍM scroll-snapem BEZ autoposunu (prst je
 * přesnější než smyčka) a hero přepíná fotky swipem s tečkami. Body 3 a 4
 * se proto na mobilu vůbec nespouští -- není to chybějící funkce, je to
 * jiná specifikace.
 *
 * MODUL NENÍ NOSITELEM VIDITELNOSTI OBSAHU. Třída `hp2026-ready` na `<html>`
 * se přidává na PRVNÍM řádku, aby CSS záchranná síť
 * (`html.js:not(.hp2026-ready)`) věděla, že modul běží. Kdyby cokoli dál
 * spadlo, obsah je vidět -- tatáž disciplína jako `reveal.js` (08-02).
 *
 * `prefers-reduced-motion` VYPÍNÁ POHYB, ne obsah: čísla naskočí rovnou na
 * finální hodnotu, pásy stojí, sekce se ukážou bez animace.
 */

import { REVEAL_IO_THRESHOLD, REVEAL_ROOT_MARGIN } from './reveal-timing.js';
import { guard } from './resize-guard.js';


/** Třída na `<html>`: „modul homepage se spustil". */
const READY_CLASS = 'hp2026-ready';

/** Scope homepage -- mimo něj modul nesahá na nic. */
const ROOT_SELECTOR = '.th2026-hp-mockup';

/** Práh mobilní vrstvy (kontrakt v1.6, mobilní návrh). */
const MOBILE_QUERY = '(max-width: 899px)';

/** Rychlost pásu lokalit v px/s (návrh ř. 1235). */
const STRIP_SPEED = 37.1875;

/** Rozteč karet pásu: šířka 212 px + mezera 26 px (návrh ř. 1237). */
const STRIP_PITCH = 238;

/** Posun driftu karuselu v px na snímek (návrh ř. 1185). */
const CAROUSEL_SPEED = -0.35;

/** Interval střídání fotek v hero v ms (návrh ř. 1251). */
const HERO_INTERVAL = 4200;

/**
 * Je aktivní mobilní vrstva?
 *
 * @return {boolean} true pod 900 px.
 */
function isMobile() {
	return matchMedia(MOBILE_QUERY).matches;
}

/**
 * Přeje si uživatel omezený pohyb?
 *
 * @return {boolean} true při `prefers-reduced-motion: reduce`.
 */
function prefersReduced() {
	return matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Ma zarizeni skutecnou hover schopnost (mys/trackpad)?
 *
 * NALEZ 1.27.13 (triaz dvojiteho tapu, typ 1 ze SO #13 -- potvrzeno).
 * `initMaptip()` nize vesela `mouseenter` na tooltip a tridu `.on`; iOS
 * Safari takovy listener pri prvnim tapu EMULUJE (aby uzivatel na mysi
 * uvidel hover stav), takze `click` prijde az na DRUHY tap. Handlery,
 * ktere jen MENI VZHLED na hover (ne skutecnou navigaci), se proto
 * pripojuji jen na hover-schopnych zarizenich -- na dotyku tenhle krok
 * uplne odpada a prvni tap rovnou spusti `click`.
 *
 * @return {boolean} true, kdyz `(hover: hover)` sedi.
 */
function hoverCapable() {
	return matchMedia('(hover: hover)').matches;
}

/**
 * Tooltip pinů mapy lokalit (návrh ř. 1145-1161).
 *
 * Obsah se skládá z textových uzlů, ne z `innerHTML`: jméno i popisek jdou
 * z `data-*` atributů, tedy z hodnot, které do stránky vypsala šablona --
 * a i tak se nesestavují jako HTML řetězec (ASVS V5, tatáž disciplína jako
 * moduly F0.5).
 *
 * @param {Element} root Kořen homepage.
 * @return {void}
 */
function initMaptip(root) {
	const box = root.querySelector('.lokmapbig');
	const tip = root.querySelector('.maptip');

	if (!box || !tip) {
		return;
	}

	const pins = Array.prototype.slice.call(box.querySelectorAll('.pin'));
	const showTip = hoverCapable();

	pins.forEach((pin) => {
		if (showTip) {
			pin.addEventListener('mouseenter', () => {
				const name = document.createTextNode(pin.getAttribute('data-name') || '');
				const sub = document.createElement('small');

				sub.textContent = pin.getAttribute('data-sub') || '';
				tip.textContent = '';
				tip.appendChild(name);
				tip.appendChild(sub);
				tip.classList.add('on');
			});

			pin.addEventListener('mouseleave', () => {
				tip.classList.remove('on');
			});
		}

		pin.addEventListener('click', () => {
			const url = pin.getAttribute('data-url');

			if (url) {
				location.href = url;
			}
		});
	});

	if (showTip) {
		box.addEventListener('mousemove', (e) => {
			const r = box.getBoundingClientRect();

			tip.style.left = `${e.clientX - r.left + 16}px`;
			tip.style.top = `${e.clientY - r.top - 14}px`;
		});
	}
}

/**
 * Showreel: klik přehraje video (návrh ř. 1163-1177).
 *
 * Desktop otevře video v překryvu, mobil ho přehraje na místě posteru
 * (mobilní návrh ř. 1415-1418) -- modal na dotykovém displeji jen ubírá
 * plochu. ID videa je v `data-yt`, adresu skládá modul (žádný vstup
 * z URL ani z DOMu, který by šel podvrhnout).
 *
 * @param {Element} root Kořen homepage.
 * @return {void}
 */
function initShowreel(root) {
	const poster = root.querySelector('.rposter');

	if (!poster) {
		return;
	}

	const id = poster.getAttribute('data-yt');

	if (!id || !/^[A-Za-z0-9_-]{6,}$/.test(id)) {
		return;
	}

	const src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;

	const makeFrame = () => {
		const frame = document.createElement('iframe');

		frame.setAttribute('src', src);
		frame.setAttribute('title', poster.getAttribute('data-title') || 'Showreel');
		frame.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen');
		frame.setAttribute('allowfullscreen', 'allowfullscreen');

		return frame;
	};

	const playInline = () => {
		const holder = document.createElement('div');

		holder.className = 'rvideo';
		holder.appendChild(makeFrame());
		poster.textContent = '';
		poster.appendChild(holder);
	};

	const openBox = () => {
		const box = document.createElement('div');
		const video = document.createElement('div');
		const close = document.createElement('button');

		box.className = 'lbox on';
		box.setAttribute('role', 'dialog');
		box.setAttribute('aria-modal', 'true');
		video.className = 'lbvideo';
		video.appendChild(makeFrame());
		close.className = 'lbclose';
		close.type = 'button';
		close.textContent = '×';
		box.appendChild(close);
		box.appendChild(video);
		document.body.appendChild(box);
		document.body.classList.add('lbox-open');
		close.focus();

		const shut = () => {
			box.parentNode.removeChild(box);
			document.body.classList.remove('lbox-open');
		};

		close.addEventListener('click', shut);
		box.addEventListener('click', (e) => {
			if (e.target === box) {
				shut();
			}
		});
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && box.parentNode) {
				shut();
			}
		});
	};

	poster.addEventListener('click', (e) => {
		e.preventDefault();

		if (isMobile()) {
			playInline();
		} else {
			openBox();
		}
	});
}

/**
 * Krok pásu = šířka první karty + mezera (stejný vzorec jako
 * `carousel.js` `stepOf`, `.rtrack` tu ale žádný nový modul nepotřebuje).
 *
 * @param {Element} track `.rtrack`.
 * @return {number} Krok v px (0, když pás nemá karty).
 */
function rtrackStep(track) {
	const first = track.firstElementChild;

	if (!first) {
		return 0;
	}

	const styles = window.getComputedStyle(track);
	const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;

	return first.getBoundingClientRect().width + gap;
}

/**
 * Šipky pásů her a recenzí NA DOTYKU (nález majitele 7. 9.: „tlačítka
 * doprava a doleva nefungují nikde" -- `.rnav.rprev`/`.rnav.rnext` jsou
 * pod 900 px viditelné, ale `initCarousels` se dřív vracel hned na
 * začátku a posluchač na ně nikdy nepověsil).
 *
 * Bez driftu a klonování -- `.rviewport` je pod 900 px nativní
 * `overflow-x: auto` scroller se `scroll-snap-type`, prst ho ovládá sám;
 * šipka jen posune o jednu kartu (`scrollBy`), stejná konvence jako
 * `carousel.js` `goTo`/`mgnav` v `index.js`.
 *
 * @param {Array<Element>} cars `.rcar` kořeny.
 * @return {void}
 */
function initCarouselNavTouch(cars) {
	cars.forEach((car) => {
		const track = car.querySelector('.rtrack');
		const view = car.querySelector('.rviewport');
		const prev = car.querySelector('.rprev');
		const next = car.querySelector('.rnext');

		if (!track || !view) {
			return;
		}

		if (prev) {
			prev.addEventListener('click', () => {
				view.scrollBy({ left: -rtrackStep(track), behavior: 'smooth' });
			});
		}

		if (next) {
			next.addEventListener('click', () => {
				view.scrollBy({ left: rtrackStep(track), behavior: 'smooth' });
			});
		}
	});
}

/**
 * Plynulý drift pásů her a recenzí (návrh ř. 1180-1204).
 *
 * Stopa se zdvojí KLONOVÁNÍM uzlů, ne přes `innerHTML += innerHTML` jako
 * návrh: řetězcová cesta by celý pás znovu parsovala a zahodila případné
 * posluchače, klonování je levnější i bezpečnější.
 *
 * NA MOBILU SE NESPOUŠTÍ (jen drift a klonování). Mobilní návrh má
 * karusely jako nativní swipe se scroll-snapem (ř. 1421-1427) -- drift
 * by prstu překážel. ŠIPKY se přesto obsloužit MUSÍ (`initCarouselNavTouch`
 * výše) -- kontrakt B zakazuje viditelné tlačítko bez obsluhy.
 *
 * @param {Element} root Kořen homepage.
 * @return {void}
 */
function initCarousels(root) {
	const cars = Array.prototype.slice.call(root.querySelectorAll('.rcar'));

	if (isMobile() || prefersReduced()) {
		initCarouselNavTouch(cars);

		return;
	}

	cars.forEach((car) => {
		const track = car.querySelector('.rtrack');
		const view = car.querySelector('.rviewport');

		if (!track || !view || !track.children.length) {
			return;
		}

		const originals = Array.prototype.slice.call(track.children);

		originals.forEach((node) => {
			const copy = node.cloneNode(true);

			copy.setAttribute('aria-hidden', 'true');

			/*
			 * KLON MUSI ZMIZET I Z POŘADÍ TABULÁTORU (nález axe-core, QA 11-06).
			 * Samotné `aria-hidden="true"` odebere kopii ze stromu pro čtečku, ale
			 * odkazy a tlačítka uvnitř dál chytá `Tab` -- axe to hlásí jako
			 * `aria-hidden-focus` (serious, 6 uzlů `article.prog` na CZ i EN
			 * homepage, změřeno `qa-matrix.cjs --axe-gate` 6. 9.). Pro uživatele
			 * klávesnice to znamená projít pás her dvakrát, podruhé po prvcích,
			 * které čtečka neohlásí. `inert` by byl přesnější, ale je mimo matici
			 * podpory (Safari 14.1), takže se pořadí tabulátoru vypíná ručně.
			 * Vizuálně se nemění nic -- `tabindex` nemá žádný vykreslovací účinek.
			 */
			copy.setAttribute('tabindex', '-1');

			Array.prototype.forEach.call(
				copy.querySelectorAll('a[href], button, input, select, textarea, [tabindex]'),
				(focusable) => {
					focusable.setAttribute('tabindex', '-1');
				}
			);

			track.appendChild(copy);
		});

		let x = null;
		let jump = 0;
		let paused = false;
		let hidden = false;
		let step = 285;

		const frame = () => {
			const half = track.scrollWidth / 2;

			if (x === null && half > 0) {
				x = -half;
				step = track.children[0].getBoundingClientRect().width + 24;
			}

			if (x !== null) {
				if (!paused && !hidden) {
					x += CAROUSEL_SPEED;
				}

				if (jump !== 0) {
					const d = jump * 0.14;

					x += d;
					jump -= d;

					if (Math.abs(jump) < 1) {
						x += jump;
						jump = 0;
					}
				}

				if (x >= 0) {
					x -= half;
				}

				if (x < -half) {
					x += half;
				}

				track.style.transform = `translateX(${x}px)`;
			}

			requestAnimationFrame(frame);
		};

		const prev = car.querySelector('.rprev');
		const next = car.querySelector('.rnext');

		if (prev) {
			prev.addEventListener('click', () => {
				jump += step;
			});
		}

		if (next) {
			next.addEventListener('click', () => {
				jump -= step;
			});
		}

		view.addEventListener('mouseenter', () => {
			paused = true;
		});

		view.addEventListener('mouseleave', () => {
			paused = false;
		});

		/*
		 * PAUZA NA SKRYTE KARTE (SO-11-HP-CODE, nalez 6). Smycka
		 * requestAnimationFrame spolehala vyhradne na to, ze prohlizec sam
		 * omezi rAF na skryte karte. Vetsina dnesnich enginu to dela, ale
		 * neni to zaruka jazyka -- explicitni pojistka stoji dva radky
		 * a na mobilu setri baterii. Pauzuje se JEN posun; po navratu na
		 * kartu pas pokracuje z teze pozice, takze pohybova brana
		 * (t0 vs t+2 s pri viditelne strance) meri dal totez.
		 *
		 * VLASTNI PRIZNAK, NE prepsani `paused`: `paused` drzi pauzu pri
		 * najeti mysi (pozadavek majitele 5. 9.). Kdyby ho visibilitychange
		 * prepisoval, navrat na kartu by rozjel pas i pod kurzorem.
		 *
		 * Prehodnoceni isMobile() pri resize se ZAMERNE nedoplnuje --
		 * meni chovani pasu za behu a je to nove rozhodnuti, ne oprava
		 * (odlozena polozka).
		 */
		document.addEventListener('visibilitychange', () => {
			hidden = document.hidden;
		});

		requestAnimationFrame(frame);
	});
}

/**
 * Pozastaví (a po dotyku zase pustí) autoběh pásu lokalit.
 *
 * 1.27.11, položka 3: dlaždice pásu jsou teď skutečné odkazy (šablona
 * `stripband.php`). Bez pauzy by se cíl pod prstem během `touchstart` ->
 * `click` stihl posunout (CSS `animation`, ne JS smyčka -- proto
 * `animationPlayState`, ne zásah do `--hp-delay`). Pauza drží JEN po dobu
 * doteku samotného pásu -- `touchend`/`touchcancel` ji uvolní, aby smyčka
 * po odklepnutí/scrollu jinam pokračovala jako předtím. Mobil (pod
 * 900 px) sem vůbec nedojde -- `initStripband()` se tam vrátí dřív, žádná
 * animace tedy neběží a nemá co pozastavovat (tam řeší přesnost dotyku
 * nativní scroll-snap).
 *
 * @param {Element} strip `.hstrip2`.
 * @return {void}
 */
function pauseStripOnTouch(strip) {
	strip.addEventListener('touchstart', function () {
		strip.style.animationPlayState = 'paused';
	}, { passive: true });

	const resume = function () {
		strip.style.animationPlayState = '';
	};

	strip.addEventListener('touchend', resume, { passive: true });
	strip.addEventListener('touchcancel', resume, { passive: true });
}

/**
 * Nekonečný pás lokalit pod hero (návrh ř. 1206-1241).
 *
 * Šablona vypíše DVA identické cykly serverově; modul jen dopočítá délku
 * jednoho cyklu, z ní odvodí trvání smyčky a fázi (posun o půl rozteče,
 * aby start nevypadal jako zaseknutí) a zapne animaci třídou `is-ready`.
 *
 * NA MOBILU SE NESPOUŠTÍ (mobilní návrh ř. 1379-1387: nativní scroll-snap,
 * dlaždice jsou skutečné odkazy beze změny -- viz `stripband.php`).
 *
 * @param {Element} root Kořen homepage.
 * @return {void}
 */
function initStripband(root) {
	const strip = root.querySelector('.hstrip2');

	if (!strip || isMobile()) {
		return;
	}

	const cycle = strip.querySelector('.hcycle');

	if (!cycle) {
		return;
	}

	const width = cycle.getBoundingClientRect().width;

	if (width <= 0) {
		return;
	}

	strip.style.setProperty('--hp-duration', `${width / STRIP_SPEED}s`);
	strip.style.setProperty('--hp-delay', `${-((width / 2 + STRIP_PITCH / 2) / STRIP_SPEED)}s`);
	strip.classList.add('is-ready');
	pauseStripOnTouch(strip);
}

/**
 * TASK 1277-1a (F4 diagnóza sekce 1): odloží stažení snímků 2 až 4 hero
 * karty, dokud nejsou skutečně potřeba.
 *
 * PROČ JS, NE `content-visibility` (CSS by stačilo -- diagnóza ho nabízela
 * jako první variantu, méně kódu). `stylelint` gate `plugin/no-
 * unsupported-browser-features` (`npm run lint:css`) `content-visibility`
 * tvrdě odmítá -- projekt drží podporu Safari 14.1+ (`docs/component-
 * contract-mobile-2026.md`), kde vlastnost neexistuje vůbec (přidána až
 * Safari 18). Zbývají tedy dvě možnosti z diagnózy: `IntersectionObserver`
 * nebo „mobil jen 1. slide". Observer zachovává STEJNÝ markup a JS chování
 * napříč jazyky/šablonami (žádná nová podmínka podle šířky viewportu v
 * PHP), proto je zvolený.
 *
 * PROČ `root: wrap`, NE výchozí `null` (viewport dokumentu). `.bslides` je
 * na mobilu vodorovný scroll pás (`overflow-x: auto`) -- vzhledem k
 * VIEWPORTU DOKUMENTU by "blízko" znamenalo totéž co dnešní nativní
 * `loading="lazy"` (proto vůbec vznikl F4 nález). Vzhledem k `.bslides`
 * (root) je snímek 2 fyzicky O CELOU ŠÍŘKU KONTEJNERU mimo viditelnou
 * část, dokud uživatel neposune.
 *
 * `rootMargin: '-5%'` -- ZÁPORNÝ, ne nulový ani kladný. Ověřeno reálným
 * Playwright/Chromium měřením (mobil 390px): flexboxové zaokrouhlení
 * šířky snímků způsobuje, že snímek 2 fyzicky PŘESAHUJE pravou hranu
 * viditelné části `.bslides` o cca 9 px (subpixelový drift součtu `flex:
 * 0 0 100%` šířek, ne chyba v CSS) -- s `rootMargin: '0px'` a `threshold:
 * 0` tenhle i sebemenší přesah STAČÍ, aby `IntersectionObserver` označil
 * snímek za "intersecting" HNED při načtení (změřeno: `hp-hero-2` se
 * stahoval do 200 ms od načtení, bez jakékoli interakce). Kladný předstih
 * (dřívější pokus, `150px`) dělal přesně totéž ve větším měřítku. Záporná
 * hodnota naopak zmenšuje testovací box dovnitř -- snímek musí být
 * viditelný aspoň z 5 % šířky `root`, ne jen o pixel přes hranu, než se
 * označí za blízký. Gate „na mobilu jen 1 obrázek před interakcí" je
 * tvrdý, `-5%` je nejmenší ověřená hodnota, která ho splňuje.
 *
 * Na desktopu (`.bslides` -- `position: relative; overflow: hidden`,
 * snímky `position: absolute; inset: 0`) leží VŠECHNY snímky přesně
 * na stejné ploše jako `root`, tedy se stanou "intersecting" prakticky
 * ihned -- žádná změna oproti dnešnímu chování (tam F4 problém nenašla).
 *
 * MARKUP: PHP (`hero.php`) u snímků s indexem >= 1 vynechává `src`/
 * `srcset` a nese je jako `data-src`/`data-srcset` (jak na `<img>`, tak na
 * `<source>`) -- bez reálného `src` prohlížeč nic nestáhne, dokud tenhle
 * kód atributy nepřepíše. Snímek 0 (LCP) žádný `data-*` nemá, hydratace
 * se ho netýká.
 *
 * FAIL-OPEN: bez `IntersectionObserver` (prakticky žádný cílový
 * prohlížeč, ale kontrakt D2 fail-open vyžaduje i tak) se hydratují
 * VŠECHNY snímky hned -- horší než observer, ale nikdy prázdný slide.
 *
 * @param {Element} root Kořen homepage.
 * @return {void}
 */
function initHeroLazySlides(root) {
	const wrap = root.querySelector('.bslides');

	if (!wrap) {
		return;
	}

	const hydrate = (slide) => {
		const nodes = [slide].concat(Array.prototype.slice.call(slide.querySelectorAll('*')));

		nodes.forEach((node) => {
			if (!node.hasAttribute) {
				return;
			}

			if (node.hasAttribute('data-srcset')) {
				node.setAttribute('srcset', node.getAttribute('data-srcset'));
				node.removeAttribute('data-srcset');
			}

			if (node.hasAttribute('data-src')) {
				node.setAttribute('src', node.getAttribute('data-src'));
				node.removeAttribute('data-src');
			}
		});
	};

	const slides = Array.prototype.slice.call(wrap.querySelectorAll('.bslide'));

	if (!('IntersectionObserver' in window)) {
		slides.forEach(hydrate);
		return;
	}

	const io = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				hydrate(entry.target);
				io.unobserve(entry.target);
			}
		});
	}, { root: wrap, rootMargin: '-5%', threshold: 0 });

	slides.forEach((slide) => {
		io.observe(slide);
	});
}

/**
 * Fotky v hero: samovolné střídání, šipky, tečky (návrh ř. 1242-1258).
 *
 * Desktop prolíná fotky třídou `on` a ovládá je kruhovými šipkami
 * (rozhodnutí majitele 5. 9. 21:40). Mobil používá nativní swipe se
 * scroll-snapem a tečky pod kartou (mobilní návrh ř. 1362-1373), takže
 * se tam nic neprolíná -- jen se dopočítává aktivní tečka.
 *
 * @param {Element} root Kořen homepage.
 * @return {void}
 */

function initHeroSlider(root) {
	const wrap = root.querySelector('.bslides');

	if (!wrap) {
		return;
	}

	const slides = Array.prototype.slice.call(wrap.querySelectorAll('.bslide'));
	const dots = Array.prototype.slice.call(root.querySelectorAll('.bdots button'));

	if (slides.length < 2) {
		return;
	}

	let index = 0;
	let timer = null;

	const mark = (n) => {
		dots.forEach((dot, i) => {
			if (i === n) {
				dot.setAttribute('aria-current', 'true');
			} else {
				dot.removeAttribute('aria-current');
			}
		});
	};

	if (isMobile()) {
		wrap.addEventListener('scroll', () => {
			const step = wrap.clientWidth || 1;

			mark(Math.round(wrap.scrollLeft / step));
		}, { passive: true });

		dots.forEach((dot, i) => {
			dot.addEventListener('click', () => {
				wrap.scrollTo({ left: i * wrap.clientWidth, behavior: prefersReduced() ? 'auto' : 'smooth' });
			});
		});

		return;
	}

	const show = (n) => {
		slides[index].classList.remove('on');
		index = (n + slides.length) % slides.length;
		slides[index].classList.add('on');
		mark(index);
	};

	const auto = () => {
		if (prefersReduced()) {
			return;
		}

		timer = window.setInterval(() => {
			show(index + 1);
		}, HERO_INTERVAL);
	};

	const restart = (n) => {
		window.clearInterval(timer);
		show(n);
		auto();
	};

	const prev = root.querySelector('.bnav.bprev');
	const next = root.querySelector('.bnav.bnext');

	if (prev) {
		prev.addEventListener('click', () => {
			restart(index - 1);
		});
	}

	if (next) {
		next.addEventListener('click', () => {
			restart(index + 1);
		});
	}

	const card = root.querySelector('.bigcard');

	if (card) {
		card.addEventListener('mouseenter', () => {
			window.clearInterval(timer);
		});
		card.addEventListener('mouseleave', auto);
	}

	auto();
}

/**
 * Vstupní animace hero (návrh ř. 1259-1264).
 *
 * Spouští se až po `document.fonts.ready`, aby kaskáda slov neběžela na
 * náhradním písmu a nepřeskočila; pojistka po 700 ms pokrývá prohlížeč
 * bez Font Loading API.
 *
 * @param {Element} root Kořen homepage.
 * @return {void}
 */
function initHeroIn(root) {
	const hero = root.querySelector('.hero');

	if (!hero) {
		return;
	}

	const go = () => {
		requestAnimationFrame(() => {
			// `classList.add()` na uz pritomnou tridu je z DOMTokenList spec
			// no-op (na rozdil od `setAttribute()` na stejnou hodnotu, viz
			// `reveal.js markReady()`) -- kontrola tu tedy nic funkcne
			// nemeni, jen sjednocuje zapis s ostatnimi misty v tomhle
			// souboru (koordinator, konzistence 1.27.13).
			if (!hero.classList.contains('in')) {
				hero.classList.add('in');
			}
		});
	};

	if (document.fonts && document.fonts.ready) {
		document.fonts.ready.then(go);
		setTimeout(go, 700);
	} else {
		go();
	}
}

/**
 * Nástup sekcí při scrollu (návrh ř. 1265-1274).
 *
 * Prvních 1200 ms po načtení dostane třídu `in` každá sekce, která je
 * VIDĚT hned -- bez toho by první obrazovka čekala na scroll, který nikdy
 * nepřijde. Zbytek řeší IntersectionObserver s prahem 0,3.
 *
 * @param {Element} root Kořen homepage.
 * @return {void}
 */
function initSectionIn(root) {
	const secs = Array.prototype.slice.call(root.querySelectorAll('.stats, .envs, .reveal'));

	if (!secs.length) {
		return;
	}

	if (!('IntersectionObserver' in window)) {
		secs.forEach((s) => {
			if (!s.classList.contains('in')) {
				s.classList.add('in');
			}
		});

		return;
	}

	setTimeout(() => {
		secs.forEach((s) => {
			const r = s.getBoundingClientRect();

			if (r.top < innerHeight && r.bottom > 0 && !s.classList.contains('in')) {
				guard(() => s.classList.add('in'));
			}
		});
	}, 1200);

	/*
	 * Zrychleny trigger (nalez majitele 7. 9. "plocha zustane bez obsahu"
	 * -- schvalena odchylka od navrhu, viz status AS): threshold 0.3 -> 0.1
	 * a rootMargin -10% zdola misto vetsiho odsazeni, aby se sekce odhalila
	 * driv po vstupu do viewportu.
	 */
	/*
	 * IGNOROVAT IO ZAZNAMY DO 300 MS PO RESIZE (1.27.13, koordinatoruv
	 * nalez, stejny mechanismus jako `pulse.js`): zmena vysky viewportu
	 * (iOS lista adresy po tapu -- 635 -> 874 px) donuti prohlizec
	 * prepocitat pruniky VSECH pozorovanych sekci, i tech, ktere se jeste
	 * nezobrazily -- IO tak muze dorucit `isIntersecting: true` PRESNE
	 * v okamziku tapu, WebKit v hover fazi mutaci (trida `in`) vezme jako
	 * zmenu obsahu a klik zahodi (majitel: "sjedu niz, vyjede CTA a 2
	 * tapy" -- stejny mechanismus, jen spoustec je tady sekce reelu, ne
	 * dock). `io.unobserve()` uz drive resil "jednou za zivot prvku";
	 * `guard()` ze sdileneho `resize-guard.js` resi DRUHOU cast: tridu
	 * NEPRIDAT, dokud se viewport po zmene velikosti neustali (odlozi ji,
	 * nezahodi -- sekce se odhali o ~300 ms pozdeji, ne nikdy).
	 */
	const io = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				io.unobserve(entry.target);
				guard(() => {
					if (!entry.target.classList.contains('in')) {
						entry.target.classList.add('in');
					}
				});
			}
		});
	}, { threshold: REVEAL_IO_THRESHOLD, rootMargin: REVEAL_ROOT_MARGIN });

	secs.forEach((s) => {
		io.observe(s);
	});
}

/**
 * Náběh čísel ze statistik (návrh ř. 1275-1299).
 *
 * Šablona vypisuje nulu a finální hodnotu drží v `data-count`; modul ji
 * dopočítá ease-out křivkou za 1,1 s se stagerem 110 ms. Při omezeném
 * pohybu se hodnota dosadí rovnou -- číslo nesmí zůstat nulové jen proto,
 * že si uživatel vypnul animace.
 *
 * @param {Element} root Kořen homepage.
 * @return {void}
 */
function initCountUp(root) {
	const wrap = root.querySelector('.stats');

	if (!wrap) {
		return;
	}

	const nums = Array.prototype.slice.call(wrap.querySelectorAll('b[data-count]'));

	if (!nums.length) {
		return;
	}

	const finish = (el) => {
		el.textContent = (el.getAttribute('data-count') || '') + (el.getAttribute('data-suffix') || '');
	};

	if (prefersReduced() || !('IntersectionObserver' in window)) {
		nums.forEach(finish);

		return;
	}

	const run = () => {
		nums.forEach((el, i) => {
			const target = parseInt(el.getAttribute('data-count'), 10);
			const sfx = el.getAttribute('data-suffix') || '';

			/*
			 * NEJEDNOZNACNE CISLO SE NEANIMUJE, DOSADI SE (11-06b, task 6).
			 * Kdyby `data-count` neslo prevest na cislo (mezera nebo jiny
			 * nenumericky znak uvnitr), `parseInt` by vratil jen prvni cast
			 * a animace by dobehla na VIDITELNE SPATNE cislo. Radeji polozka
			 * bez animace nez spatna hodnota. Dnesni data tenhle pripad
			 * nemaji -- zmereno 6. 9.: `number` je 16 / 300 / 120 / 30
			 * a `suffix` nese zbytek (`+`, ` 000+`).
			 */
			if (isNaN(target)) {
				finish(el);

				return;
			}

			/*
			 * NULU NASTAVUJE AZ JS (progressive enhancement, 11-06b).
			 * Serverove HTML nese od 11-06b uz CILOVOU hodnotu, aby ji
			 * precetl i ctenar bez JS, crawler a model. Animace proto
			 * zacina tim, ze si nulu dosadi sama -- vizualne je vysledek
			 * stejny jako predtim.
			 */
			el.textContent = '0' + sfx;

			const dur = 1100;
			const delay = i * 110;
			let start = null;

			const step = (ts) => {
				if (!start) {
					start = ts;
				}

				const t = (ts - start - delay) / dur;

				if (t < 0) {
					requestAnimationFrame(step);

					return;
				}

				if (t >= 1) {
					el.textContent = target + sfx;

					return;
				}

				el.textContent = Math.round(target * (1 - Math.pow(1 - t, 3))) + sfx;
				requestAnimationFrame(step);
			};

			requestAnimationFrame(step);
		});
	};

	const io = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				io.disconnect();
				// Count-up prepisuje `<b>` kazdy snimek (~100 mutaci) -- po
				// zmene vysky viewportu pocka, az se ustali (resize-guard.js).
				guard(run);
			}
		});
	}, { threshold: 0.4 });

	io.observe(wrap);
}

/** Kolik po sobe jdoucich snimku musi byt pozice cile stabilni (viz nize). */
const SETTLE_STABLE_FRAMES = 6;

/** Bezpecnostni strop snimku, kdyby se pozice nikdy neustalila. */
const SETTLE_MAX_FRAMES = 90;

/** Pod touto zmenou (px) mezi snimky se pozice pocita jako "stejna". */
const SETTLE_EPSILON = 0.5;

/**
 * Doscrolluje kotvu homepage po studenem loadu s hashem primo v URL
 * (1.27.11, polozka 2, druha cast -- brana "kotva funguje i z podstranky").
 *
 * CSS `scroll-margin-top` (`.jak-to-probiha-anchor`, vyse v tomto souboru) resi
 * klik BEHEM session (menu, horni navigace) spolehlive -- zmereno gap
 * ~0px. Cerstvy load primo na `/#cil` ale prohlizec scrolluje na zaklade
 * geometrie cile V OKAMZIKU SKOKU, a `.reveal` sekce (viz `components.css`
 * `html.js .reveal { transform: translateY(12px) }`) v tu chvili jeste
 * MUZE byt v pocatecnim, needhalenem stavu, nez ji `reveal.js` odhali.
 * Kdyz se to stane, transform zmeni vizualni pozici cile PO tom, co uz
 * prohlizec doscrolloval -- a nic tu pozici dodatecne neopravi samo.
 *
 * PROC POLLING NA `requestAnimationFrame`, NE JEDEN VYSTREL NA `load`
 * (zmereno Playwrightem, 5 nezavislych behu): prohlizec umi "skok na
 * fragment" pretriggerovat VICKRAT behem nacitani stranky, a NE VZDY
 * pred udalosti `load` -- jeden pokus hned na `load` (nebo i s pevnym
 * odstupem 150 ms) v casti behu prohlizec o kousek pozdeji prepsal svym
 * vlastnim (chybnym) skokem zpatky. Reseni neni "pockat dost dlouho", ale
 * pockat, AZ SE POZICE CILE PRESTANE MENIT ( >= 6 snimku po sobe beze
 * zmeny vetsi nez 0,5 px) -- teprve pak je jiste, ze uz nic dalsiho
 * neprebehne, a `scrollIntoView()` je bezpecne poslednim slovem.
 * `behavior: 'instant'` zamerne, ne `'auto'` (= `scroll-behavior: smooth`
 * z chrome.css): animovany dojezd by sam o sobe trval pres sekundu a
 * prodlouzil by okno, ve kterem muze prohlizeci pretriggrovani zavod
 * vyhrat.
 *
 * SCOPED JEN NA CILE S `.reveal`/`.reveal--left`/`.reveal--blur` -- to je
 * presne mnozina prvku s pocatecnim transformem, tedy jedina, u ktere
 * tenhle jev muze nastat. `#poptavka` (formsec.php) napr. `.reveal` NEMA,
 * takze ho tahle funkce nenajde a nic pro nej nedela (fail-open D4, ne
 * hardcoded jedno ID -- kdyby v budoucnu pribyla dalsi kotva na `.reveal`
 * sekci, oprava uz na ni plati taky).
 *
 * @param {Element} root Koren homepage.
 * @return {void}
 */
function settleHashAnchor(root) {
	const id = location.hash.length > 1 ? location.hash.slice(1) : '';

	if (!id) {
		return;
	}

	const target = document.getElementById(id);

	if (!target || !root.contains(target)) {
		return;
	}

	const cls = target.classList;

	if (!cls.contains('reveal') && !cls.contains('reveal--left') && !cls.contains('reveal--blur')) {
		return;
	}

	const waitForStablePosition = function () {
		let stableFrames = 0;
		let lastTop = null;
		let frames = 0;

		const tick = function () {
			frames += 1;

			const top = target.getBoundingClientRect().top;

			if (null !== lastTop && Math.abs(top - lastTop) < SETTLE_EPSILON) {
				stableFrames += 1;
			} else {
				stableFrames = 0;
			}

			lastTop = top;

			if (stableFrames >= SETTLE_STABLE_FRAMES || frames >= SETTLE_MAX_FRAMES) {
				target.scrollIntoView({ behavior: 'instant', block: 'start' });

				return;
			}

			requestAnimationFrame(tick);
		};

		requestAnimationFrame(tick);
	};

	if ('complete' === document.readyState) {
		waitForStablePosition();
	} else {
		window.addEventListener('load', waitForStablePosition, { once: true });
	}
}

/**
 * Spustí pohyb homepage.
 *
 * Mimo homepage nedělá nic: bez kořenového prvku modul rovnou skončí,
 * takže se na ostatních šablonách nepřipojí ani jeden posluchač.
 *
 * @return {void}
 */
export function init() {
	const root = document.querySelector(ROOT_SELECTOR);

	if (!root) {
		return;
	}

	document.documentElement.classList.add(READY_CLASS);

	initHeroIn(root);
	initSectionIn(root);
	initHeroLazySlides(root);
	initHeroSlider(root);
	initStripband(root);
	initCarousels(root);
	initCountUp(root);
	initMaptip(root);
	initShowreel(root);
	settleHashAnchor(root);
}
